import { useEffect, useState } from 'react';
import { BookOpen, ChevronDown, UserRound, Users } from 'lucide-react';

import {
  InstructorRadarChart,
  type InstructorRadarChartDataset,
} from '../components/ui/Charts/InstructorRadarChart';
import { InstructorLoadingSkeleton } from '../components/layout/InstructorLoadingSkeleton';
import {
  instructorService,
  type CourseStudent,
  type CourseSubmission,
  type Dimension,
  type InstructorCourse,
  type SubmissionDetails,
} from '../services/instructorService';
import { formatEnglishDate } from '../lib/dateFormat';

type DashboardScope = 'individual' | 'group';
type DimensionCategory =
  | 'Purposefulness'
  | 'Disciplinary'
  | 'Integration'
  | 'Critical Awareness';
type ScoreMap = Record<number, number>;

interface SubmissionMarker {
  id: string;
  submittedAt: string | null;
}

interface SubmissionSnapshot extends SubmissionMarker {
  scores: ScoreMap;
  scoreCount: number;
}

interface DashboardEntity {
  id: string;
  label: string;
  avatarLabel: string;
  groupLabel: string;
  memberNames: string[];
  color: string;
  submissions: number;
  submissionSnapshots: SubmissionSnapshot[];
  lastSubmitted: string | null;
  scores: ScoreMap;
  previousScores: ScoreMap;
  scoreCount: number;
  previousScoreCount: number;
}

const dimensionCategories: DimensionCategory[] = [
  'Purposefulness',
  'Disciplinary',
  'Integration',
  'Critical Awareness',
];

const categoryColors: Record<DimensionCategory, string> = {
  Purposefulness: '#003D7C',
  Disciplinary: '#0D7377',
  Integration: '#B8860B',
  'Critical Awareness': '#C8102E',
};

const dimensionCategoryById: Record<number, DimensionCategory> = {
  1: 'Purposefulness',
  2: 'Purposefulness',
  3: 'Disciplinary',
  4: 'Disciplinary',
  5: 'Disciplinary',
  6: 'Integration',
  7: 'Integration',
  8: 'Critical Awareness',
  9: 'Critical Awareness',
};

const entityColors = ['#003D7C', '#0F766E', '#B45309', '#5B21B6', '#C8102E'];

const getInitials = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

const formatShortDate = (dateString: string | null) => {
  if (!dateString) {
    return 'No submissions';
  }

  return formatEnglishDate(dateString, {
    day: 'numeric',
    month: 'short',
  });
};

const getNumericScore = (score: unknown) => {
  const value = Number(score);

  return Number.isFinite(value) ? value : 0;
};

const getDimensionCategory = (
  dimension: Dimension,
  index: number
): DimensionCategory => {
  const configuredCategory = dimensionCategoryById[dimension.id];

  if (configuredCategory) {
    return configuredCategory;
  }

  const label = `${dimension.label} ${dimension.short_label || ''}`.toLowerCase();

  if (label.includes('frame') || label.includes('stakeholder')) {
    return 'Purposefulness';
  }

  if (
    label.includes('range') ||
    label.includes('reason') ||
    label.includes('credib') ||
    label.includes('disciplin')
  ) {
    return 'Disciplinary';
  }

  if (label.includes('integ') || label.includes('number') || label.includes('depth')) {
    return 'Integration';
  }

  if (label.includes('social') || label.includes('limit') || label.includes('critical')) {
    return 'Critical Awareness';
  }

  if (index <= 1) {
    return 'Purposefulness';
  }

  if (index <= 4) {
    return 'Disciplinary';
  }

  if (index <= 6) {
    return 'Integration';
  }

  return 'Critical Awareness';
};

const getCourseDimensions = (
  course: InstructorCourse | null,
  dimensions: Dimension[]
) => {
  const courseDimensionIds = course?.dimension_ids || [];

  if (courseDimensionIds.length === 0) {
    return dimensions.filter((dimension) => dimension.is_active);
  }

  return courseDimensionIds
    .map((dimensionId) => dimensions.find((dimension) => dimension.id === dimensionId))
    .filter((dimension): dimension is Dimension => Boolean(dimension));
};

const createEmptyScoreMap = (dimensionIds: number[]) => {
  const scores: ScoreMap = {};

  dimensionIds.forEach((dimensionId) => {
    scores[dimensionId] = 0;
  });

  return scores;
};

const createScoreMap = (
  details: SubmissionDetails | null,
  dimensionIds: number[]
) => {
  const scores = createEmptyScoreMap(dimensionIds);

  if (!details) {
    return scores;
  }

  details.scores.forEach((score) => {
    if (dimensionIds.includes(score.dimension_id)) {
      scores[score.dimension_id] = getNumericScore(score.score);
    }
  });

  return scores;
};

const sortSubmissionsDesc = <T extends { submitted_at: string }>(items: T[]) =>
  [...items].sort(
    (first, second) =>
      new Date(second.submitted_at).getTime() -
      new Date(first.submitted_at).getTime()
  );

const getScoreTotal = (scores: ScoreMap, activeDimensionIds: number[]) =>
  activeDimensionIds.reduce((sum, dimensionId) => sum + (scores[dimensionId] || 0), 0);

const getAverageScoreMapForSnapshots = (
  snapshots: SubmissionSnapshot[],
  dimensionIds: number[]
) => {
  const scores = createEmptyScoreMap(dimensionIds);
  const scoredSnapshots = snapshots.filter((snapshot) => snapshot.scoreCount > 0);

  if (scoredSnapshots.length === 0) {
    return scores;
  }

  dimensionIds.forEach((dimensionId) => {
    scores[dimensionId] =
      scoredSnapshots.reduce(
        (sum, snapshot) => sum + (snapshot.scores[dimensionId] || 0),
        0
      ) / scoredSnapshots.length;
  });

  return scores;
};

const getClampedSubmissionIndex = (
  selectedSubmissionIndex: number | null,
  submissionCount: number
) => {
  if (submissionCount === 0) {
    return -1;
  }

  if (selectedSubmissionIndex === null) {
    return submissionCount - 1;
  }

  return Math.min(Math.max(selectedSubmissionIndex, 0), submissionCount - 1);
};

const getWeakestDimension = (
  scores: ScoreMap,
  dimensions: Dimension[]
) => {
  if (dimensions.length === 0) {
    return null;
  }

  return dimensions.reduce((weakest, dimension) =>
    (scores[dimension.id] || 0) < (scores[weakest.id] || 0) ? dimension : weakest
  );
};

const getTrend = (entity: DashboardEntity, activeDimensionIds: number[]) => {
  if (entity.previousScoreCount === 0 || entity.scoreCount === 0) {
    return 'Flat';
  }

  const latestTotal = getScoreTotal(entity.scores, activeDimensionIds);
  const previousTotal = getScoreTotal(entity.previousScores, activeDimensionIds);

  if (latestTotal > previousTotal + 0.5) {
    return 'Up';
  }

  if (latestTotal < previousTotal - 0.5) {
    return 'Down';
  }

  return 'Flat';
};

const getStudentSubmissions = (
  student: CourseStudent,
  submissions: CourseSubmission[]
) => { 
  //explicit declarations to match submission to student
  //otherwise the labels end up taking from latest submission overall
  const studentIdStr = student.student_id ? String(student.student_id) : null;
  const studentEmail = student.email ? student.email.toLowerCase().trim() : null;

  return submissions.filter((submission) => {
    // Check direct ownership by student_id or email
    const matchesDirectId =
      studentIdStr && submission.student_id
        ? String(submission.student_id) === studentIdStr
        : false;

    const matchesEmail =
      studentEmail && submission.student_email
        ? submission.student_email.toLowerCase().trim() === studentEmail
        : false;

    // Check if student is part of a group submission's participant list
    const matchesParticipants =
      studentIdStr && Array.isArray(submission.participant_student_ids)
        ? submission.participant_student_ids.some(
            (id) => String(id) === studentIdStr
          )
        : false;

    return matchesDirectId || matchesEmail || matchesParticipants;
  });
};

const getScoredDetailsForSubmissions = (
  submissions: CourseSubmission[],
  detailsBySubmissionId: Record<string, SubmissionDetails>
) =>
  sortSubmissionsDesc(submissions)
    .map((submission) => detailsBySubmissionId[submission.id] ?? null)
    .filter(
      (details): details is SubmissionDetails =>
        Boolean(details) && details.scores.length > 0
    );

const getSubmissionSnapshots = (
  submissions: CourseSubmission[],
  detailsBySubmissionId: Record<string, SubmissionDetails>,
  dimensionIds: number[]
) =>
  submissions
    .slice()
    .reverse()
    .map((submission) => {
      const details = detailsBySubmissionId[submission.id] ?? null;

      return {
        id: submission.id,
        submittedAt: submission.submitted_at,
        scores: createScoreMap(details, dimensionIds),
        scoreCount: details?.scores.length ?? 0,
      };
    });

const buildStudentEntities = (
  students: CourseStudent[],
  submissions: CourseSubmission[],
  detailsBySubmissionId: Record<string, SubmissionDetails>,
  dimensionIds: number[]
): DashboardEntity[] =>
  students.map((student, index) => {
    const studentSubmissions = sortSubmissionsDesc(
      getStudentSubmissions(student, submissions)
    );
    const scoredDetails = getScoredDetailsForSubmissions(
      studentSubmissions,
      detailsBySubmissionId
    );
    const latestDetails = scoredDetails[0] ?? null;
    const previousDetails = scoredDetails[1] ?? null;
    const latestSubmission = studentSubmissions[0] ?? null;
    // const latestGroupSubmission = studentSubmissions.find(
    //   (submission) => submission.project_type === 'group'
    // );
    const groupLabel = latestSubmission?.project_type === 'group'
      ? 'Group'
      : latestSubmission?.project_type === 'individual'
        ? 'Individual'
        : 'No submissions';

    return {
      id: student.id,
      label: student.display_name,
      avatarLabel: getInitials(student.display_name),
      groupLabel,
      memberNames: [student.display_name],
      color: entityColors[index % entityColors.length],
      submissions: student.submission_count,
      submissionSnapshots: getSubmissionSnapshots(
        studentSubmissions,
        detailsBySubmissionId,
        dimensionIds
      ),
      lastSubmitted: student.last_submission_at || latestSubmission?.submitted_at || null,
      scores: createScoreMap(latestDetails, dimensionIds),
      previousScores: createScoreMap(previousDetails, dimensionIds),
      scoreCount: latestDetails?.scores.length ?? 0,
      previousScoreCount: previousDetails?.scores.length ?? 0,
    };
  });

const buildGroupEntities = (
  submissions: CourseSubmission[],
  detailsBySubmissionId: Record<string, SubmissionDetails>,
  dimensionIds: number[]
): DashboardEntity[] => {
  const projectIds = Array.from(new Set(submissions.map((submission) => submission.project_id)));

  return projectIds.map((projectId, index) => {
    const projectSubmissions = sortSubmissionsDesc(
      submissions.filter((submission) => submission.project_id === projectId)
    );
    const latestSubmission = projectSubmissions[0] ?? null;
    const scoredDetails = getScoredDetailsForSubmissions(
      projectSubmissions,
      detailsBySubmissionId
    );
    const latestDetails = scoredDetails[0] ?? null;
    const previousDetails = scoredDetails[1] ?? null;
    const memberNames = latestSubmission?.participant_names?.filter(Boolean) ?? [];
    const participantCount = memberNames.length || 1;

    return {
      id: projectId,
      label: latestSubmission?.project_title || 'Project',
      avatarLabel: latestSubmission?.project_type === 'group' ? `G${index + 1}` : 'I',
      groupLabel:
        latestSubmission?.project_type === 'group'
          ? `${participantCount} members`
          : 'Individual project',
      memberNames:
        memberNames.length > 0
          ? memberNames
          : [latestSubmission?.student_name || 'Unknown student'],
      color: entityColors[index % entityColors.length],
      submissions: projectSubmissions.length,
      submissionSnapshots: getSubmissionSnapshots(
        projectSubmissions,
        detailsBySubmissionId,
        dimensionIds
      ),
      lastSubmitted: latestSubmission?.submitted_at ?? null,
      scores: createScoreMap(latestDetails, dimensionIds),
      previousScores: createScoreMap(previousDetails, dimensionIds),
      scoreCount: latestDetails?.scores.length ?? 0,
      previousScoreCount: previousDetails?.scores.length ?? 0,
    };
  });
};

const getAverageScoreMapForSubmissionIndex = (
  entities: DashboardEntity[],
  dimensionIds: number[],
  submissionIndex: number
) => {
  const scores = createEmptyScoreMap(dimensionIds);

  if (submissionIndex < 0) {
    return scores;
  }

  const scoredSnapshots = entities
    .map((entity) => entity.submissionSnapshots[submissionIndex] ?? null)
    .filter(
      (snapshot): snapshot is SubmissionSnapshot =>
        Boolean(snapshot) && snapshot.scoreCount > 0
    );

  if (scoredSnapshots.length === 0) {
    return scores;
  }

  dimensionIds.forEach((dimensionId) => {
    scores[dimensionId] =
      scoredSnapshots.reduce(
        (sum, snapshot) => sum + (snapshot.scores[dimensionId] || 0),
        0
      ) / scoredSnapshots.length;
  });

  return scores;
};

const getScoredEntitiesForSubmissionIndex = (
  entities: DashboardEntity[],
  submissionIndex: number
) => {
  if (submissionIndex < 0) {
    return [];
  }

  return entities.filter(
    (entity) => (entity.submissionSnapshots[submissionIndex]?.scoreCount ?? 0) > 0
  );
};

const getEntityCountLabel = (count: number, scope: DashboardScope) => {
  if (scope === 'individual') {
    return `${count} ${count === 1 ? 'student' : 'students'}`;
  }

  return `${count} ${count === 1 ? 'project' : 'projects'}`;
};

const getIncludedEntitySummary = (entities: DashboardEntity[]) => {
  if (entities.length === 0) {
    return 'No scored submissions included';
  }

  const visibleLabels = entities.slice(0, 8).map((entity) => entity.label);
  const hiddenCount = entities.length - visibleLabels.length;
  const suffix = hiddenCount > 0 ? `, and ${hiddenCount} more` : '';

  return `${visibleLabels.join(', ')}${suffix}`;
};

const getSubmissionMarkerDate = (
  entities: DashboardEntity[],
  submissionIndex: number
) => {
  const dates = entities
    .map((entity) => entity.submissionSnapshots[submissionIndex]?.submittedAt ?? null)
    .filter((date): date is string => Boolean(date))
    .sort(
      (first, second) =>
        new Date(first).getTime() - new Date(second).getTime()
    );

  return dates[dates.length - 1] ?? null;
};

const InstructorDashboard = () => {
  const [courses, setCourses] = useState<InstructorCourse[]>([]);
  const [dimensions, setDimensions] = useState<Dimension[]>([]);
  const [students, setStudents] = useState<CourseStudent[]>([]);
  const [submissions, setSubmissions] = useState<CourseSubmission[]>([]);
  const [detailsBySubmissionId, setDetailsBySubmissionId] = useState<Record<string, SubmissionDetails>>({});
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [scope, setScope] = useState<DashboardScope>('individual');
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [requestedSubmissionIndex, setRequestedSubmissionIndex] = useState<number | null>(null);
  const [activeDimensionIds, setActiveDimensionIds] = useState<number[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [courseLoading, setCourseLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedCourse =
    courses.find((course) => course.id === selectedCourseId) ?? null;
  const courseDimensions = getCourseDimensions(selectedCourse, dimensions);
  const courseDimensionIds = courseDimensions.map((dimension) => dimension.id);
  const maxScore = courseDimensionIds.length * 3;
  const individualEntities = buildStudentEntities(
    students,
    submissions,
    detailsBySubmissionId,
    courseDimensionIds
  );
  const groupEntities = buildGroupEntities(
    submissions,
    detailsBySubmissionId,
    courseDimensionIds
  );
  const entities = scope === 'individual' ? individualEntities : groupEntities;
  const averageLabel = scope === 'individual' ? 'Class Avg' : 'Project Avg';
  const averageSubject = scope === 'individual' ? 'class average' : 'project average';
  const overviewEntityLabel = scope === 'individual' ? 'students' : 'projects';
  const selectedEntity =
    entities.find((entity) => entity.id === selectedEntityId) ?? null;
  const submissionCount = selectedEntity
    ? selectedEntity.submissionSnapshots.length
    : Math.max(0, ...entities.map((entity) => entity.submissionSnapshots.length));
  const activeSubmissionIndex = getClampedSubmissionIndex(
    requestedSubmissionIndex,
    submissionCount
  );
  const selectedSubmissionSnapshot =
    selectedEntity && activeSubmissionIndex >= 0
      ? selectedEntity.submissionSnapshots[activeSubmissionIndex] ?? null
      : null;
  const selectedRadarEntity =
    selectedEntity && selectedSubmissionSnapshot
      ? {
          ...selectedEntity,
          scores: selectedSubmissionSnapshot.scores,
          scoreCount: selectedSubmissionSnapshot.scoreCount,
        }
      : selectedEntity;
  const radarAverageScores = getAverageScoreMapForSubmissionIndex(
    entities,
    courseDimensionIds,
    activeSubmissionIndex
  );
  const averageIncludedEntities = getScoredEntitiesForSubmissionIndex(
    entities,
    activeSubmissionIndex
  );
  const averageIncludedCountLabel = getEntityCountLabel(
    averageIncludedEntities.length,
    scope
  );
  const scoredOverviewEntities = entities.filter((entity) => entity.scoreCount > 0);
  const overviewAverageScore =
    scoredOverviewEntities.length > 0
      ? scoredOverviewEntities.reduce(
          (sum, entity) => sum + getScoreTotal(entity.scores, courseDimensionIds),
          0
        ) / scoredOverviewEntities.length
      : 0;
  const previousScoredOverviewEntities = entities.filter(
    (entity) => entity.previousScoreCount > 0
  );
  const previousOverviewAverageScore =
    previousScoredOverviewEntities.length > 0
      ? previousScoredOverviewEntities.reduce(
          (sum, entity) =>
            sum + getScoreTotal(entity.previousScores, courseDimensionIds),
          0
        ) / previousScoredOverviewEntities.length
      : overviewAverageScore;
  const latestSubmission = sortSubmissionsDesc(submissions)[0] ?? null;
  const submissionMarkers: SubmissionMarker[] = Array.from(
    { length: submissionCount },
    (_, index) => {
      if (selectedEntity) {
        const snapshot = selectedEntity.submissionSnapshots[index];

        return {
          id: snapshot?.id ?? `${selectedEntity.id}-${index}`,
          submittedAt: snapshot?.submittedAt ?? null,
        };
      }

      return {
        id: `${averageLabel}-${index}`,
        submittedAt: getSubmissionMarkerDate(entities, index),
      };
    }
  );
  const selectedSubmissionStatus = selectedEntity
    ? selectedSubmissionSnapshot
      ? activeSubmissionIndex === selectedEntity.submissionSnapshots.length - 1
        ? 'Latest selected'
        : `Submission ${activeSubmissionIndex + 1} selected`
      : 'No submissions'
    : submissionCount > 0
      ? `${averageLabel} submission ${activeSubmissionIndex + 1} selected - based on ${averageIncludedCountLabel}`
      : 'No submissions';
  const radarDimensions = courseDimensions.filter((dimension) =>
    activeDimensionIds.includes(dimension.id)
  );
  const radarLabels = radarDimensions.map(
    (dimension) => dimension.short_label || dimension.label
  );
  const radarDatasets: InstructorRadarChartDataset[] = [
    {
      backgroundColor: 'rgba(148,163,184,0.10)',
      borderColor: '#94A3B8',
      borderDash: [5, 4],
      borderWidth: 1.6,
      data: radarDimensions.map((dimension) => radarAverageScores[dimension.id] || 0),
      label: averageLabel,
      pointBackgroundColor: 'rgba(148,163,184,.6)',
      pointHoverRadius: 4,
      pointRadius: 2.5,
    },
  ];

  if (selectedRadarEntity && selectedRadarEntity.scoreCount > 0) {
    radarDatasets.push({
      backgroundColor: `${selectedRadarEntity.color}26`,
      borderColor: selectedRadarEntity.color,
      borderWidth: 2.2,
      data: radarDimensions.map(
        (dimension) => selectedRadarEntity.scores[dimension.id] || 0
      ),
      label: selectedRadarEntity.label,
      pointBackgroundColor: selectedRadarEntity.color,
      pointHoverRadius: 5,
      pointRadius: 3.5,
    });
  }
  const progressionPreviousScores = selectedEntity
    ? activeSubmissionIndex > 0
      ? selectedEntity.submissionSnapshots[activeSubmissionIndex - 1]?.scores ??
        createEmptyScoreMap(courseDimensionIds)
      : createEmptyScoreMap(courseDimensionIds)
    : getAverageScoreMapForSubmissionIndex(
        entities,
        courseDimensionIds,
        activeSubmissionIndex - 1
      );
  const progressionLatestScores = selectedRadarEntity
    ? selectedRadarEntity.scores
    : radarAverageScores;
  const progressionSubtitle =
    activeSubmissionIndex < 0
      ? `No submissions - ${averageSubject}`
      : selectedEntity
        ? activeSubmissionIndex > 0
          ? `Submission ${activeSubmissionIndex + 1} vs previous submission - ${selectedEntity.label}`
          : `First submission - ${selectedEntity.label}`
        : activeSubmissionIndex > 0
          ? `Submission ${activeSubmissionIndex + 1} vs previous submission - ${averageSubject}`
          : `First submission - ${averageSubject}`;
  const scoredAnalyticsEntities = scoredOverviewEntities;
  const progressionRows = courseDimensions.map((dimension) => ({
    dimension,
    previous: progressionPreviousScores[dimension.id] || 0,
    latest: progressionLatestScores[dimension.id] || 0,
  }));
  const responseCount = entities.filter((entity) => entity.submissions > 0).length;
  const responseLabel = `${responseCount} ${overviewEntityLabel} with submissions`;
  const scoredWorkLabel = `${scoredOverviewEntities.length} ${overviewEntityLabel} with scored work`;
  const weaknessRows = courseDimensions
    .map((dimension) => {
      const counts: [number, number, number, number] = [0, 0, 0, 0];

      scoredAnalyticsEntities.forEach((entity) => {
        const level = Math.max(0, Math.min(3, Math.round(entity.scores[dimension.id] || 0)));
        counts[level] += 1;
      });

      return {
        dimension,
        counts,
        average:
          scoredAnalyticsEntities.length > 0
            ? scoredAnalyticsEntities.reduce(
                (sum, entity) => sum + (entity.scores[dimension.id] || 0),
                0
              ) / scoredAnalyticsEntities.length
            : 0,
      };
    })
    .sort((first, second) => first.average - second.average);

  useEffect(() => {
    let isMounted = true;

    const fetchBaseData = async () => {
      try {
        setInitialLoading(true);
        setError(null);
        const [coursesData, dimensionsData] = await Promise.all([
          instructorService.getCourses(),
          instructorService.getDimensions(),
        ]);

        if (!isMounted) {
          return;
        }

        setCourses(coursesData);
        setDimensions(dimensionsData);
        setSelectedCourseId(coursesData[0]?.id ?? '');
      } catch (err) {
        console.error('Failed to load instructor dashboard:', err);

        if (isMounted) {
          setError('Failed to load dashboard data');
        }
      } finally {
        if (isMounted) {
          setInitialLoading(false);
        }
      }
    };

    fetchBaseData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const currentCourse =
      courses.find((course) => course.id === selectedCourseId) ?? null;
    const ids = getCourseDimensions(currentCourse, dimensions).map(
      (dimension) => dimension.id
    );

    setActiveDimensionIds(ids);
    setSelectedEntityId(null);
    setRequestedSubmissionIndex(null);
  }, [selectedCourseId, courses, dimensions]);

  useEffect(() => {
    if (!selectedCourseId) {
      setStudents([]);
      setSubmissions([]);
      setDetailsBySubmissionId({});
      return;
    }

    let isMounted = true;

    const fetchCourseAnalytics = async () => {
      try {
        setCourseLoading(true);
        setError(null);
        const [studentsData, submissionsData] = await Promise.all([
          instructorService.getCourseStudents(selectedCourseId),
          instructorService.getCourseSubmissions(selectedCourseId),
        ]);
        const sortedSubmissions = sortSubmissionsDesc(submissionsData);
        const detailEntries: (readonly [string, SubmissionDetails] | null)[] =
          await Promise.all(
            sortedSubmissions.map(async (submission) => {
              try {
                return [
                  submission.id,
                  await instructorService.getSubmissionDetails(submission.id),
                ] as const;
              } catch (err) {
                console.error('Failed to load submission analytics:', err);
                return null;
              }
            })
          );
        const nextDetailsBySubmissionId: Record<string, SubmissionDetails> = {};

        detailEntries.forEach((entry) => {
          if (entry) {
            nextDetailsBySubmissionId[entry[0]] = entry[1];
          }
        });

        if (!isMounted) {
          return;
        }

        setStudents(studentsData);
        setSubmissions(sortedSubmissions);
        setDetailsBySubmissionId(nextDetailsBySubmissionId);
      } catch (err) {
        console.error('Failed to load course analytics:', err);

        if (isMounted) {
          setError('Failed to load course analytics');
        }
      } finally {
        if (isMounted) {
          setCourseLoading(false);
        }
      }
    };

    fetchCourseAnalytics();

    return () => {
      isMounted = false;
    };
  }, [selectedCourseId]);

  const handleDimensionToggle = (dimensionId: number) => {
    setActiveDimensionIds((current) => {
      if (current.includes(dimensionId)) {
        if (current.length <= 2) {
          return current;
        }

        return current.filter((id) => id !== dimensionId);
      }

      return [...current, dimensionId];
    });
  };

  const handleEntitySelect = (entityId: string) => {
    if (selectedEntityId !== entityId) {
      setRequestedSubmissionIndex(null);
    }

    setSelectedEntityId(entityId);
  };

  const clearSelectedEntity = () => {
    setSelectedEntityId(null);
    setRequestedSubmissionIndex(null);
  };

  if (initialLoading) {
    return (
      <div className="instructor-page instructor-design-page">
        <InstructorLoadingSkeleton message="Loading instructor dashboard..." />
      </div>
    );
  }

  return (
    <div className="instructor-page instructor-design-page">
      <div className="instructor-top-bar">
        <div className="instructor-dashboard-heading">
          <h1 className="instructor-page-title">Instructor Dashboard</h1>
          <p className="instructor-page-sub">
            Review submissions, scores, and progress across your courses.
          </p>
        </div>
      </div>

      {error && <div className="instructor-error-state compact">{error}</div>}

      {courses.length === 0 ? (
        <div className="instructor-card">
          <div className="instructor-empty-state">
            Create a course before viewing instructor analytics.
          </div>
        </div>
      ) : (
        <>
          <div className="instructor-dashboard-controls">
            <div className="instructor-dashboard-course-control">
              <span className="instructor-dashboard-control-label">Select course</span>
              <label className="instructor-course-picker" title="Switch course">
                <BookOpen strokeWidth={2} />
                <select
                  aria-label="Select course"
                  onChange={(event) => setSelectedCourseId(event.target.value)}
                  value={selectedCourseId}
                >
                  {courses.length === 0 && <option value="">No courses</option>}
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.code} - {course.title}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  aria-hidden="true"
                  className="instructor-course-picker-chevron"
                  strokeWidth={2.4}
                />
              </label>
            </div>
            <div className="instructor-scope-tabs" role="tablist">
              <button
                className={scope === 'individual' ? 'active' : ''}
                onClick={() => {
                  setScope('individual');
                  clearSelectedEntity();
                }}
                type="button"
              >
                <UserRound strokeWidth={2} />
                Individual
              </button>
              <button
                className={scope === 'group' ? 'active' : ''}
                onClick={() => {
                  setScope('group');
                  clearSelectedEntity();
                }}
                type="button"
              >
                <Users strokeWidth={2} />
                Projects
              </button>
            </div>
          </div>

          <div className="instructor-stat-row instructor-overview-stats">
            <div className="instructor-stat-card navy">
              <div className="instructor-stat-label">
                {scope === 'individual' ? 'Total Students' : 'Total Projects'}
              </div>
              <div className="instructor-stat-value">
                {scope === 'individual' ? students.length : groupEntities.length}
              </div>
              <div className="instructor-stat-sub">
                {responseLabel}
              </div>
            </div>
            <div className="instructor-stat-card">
              <div className="instructor-stat-label">Latest Submission</div>
              <div className="instructor-stat-value small">
                {latestSubmission?.project_title || 'None'}
              </div>
              <div className="instructor-stat-sub">
                {latestSubmission ? formatShortDate(latestSubmission.submitted_at) : 'No submissions'}
              </div>
            </div>
            <div className="instructor-stat-card">
              <div className="instructor-stat-label">
                {scope === 'individual' ? 'Class Avg Score' : 'Project Avg Score'}
              </div>
              <div className="instructor-stat-value">
                {overviewAverageScore.toFixed(1)}
                <span className="instructor-score-denom">/{maxScore}</span>
              </div>
              <div className="instructor-stat-sub">
                <span className="instructor-up-text">
                  {(overviewAverageScore - previousOverviewAverageScore) >= 0 ? '↑' : '↓'}{' '}
                  {Math.abs(overviewAverageScore - previousOverviewAverageScore).toFixed(1)}
                </span>{' '}
                from previous submission
              </div>
            </div>
            <div className="instructor-stat-card">
              <div className="instructor-stat-label">Total Submissions</div>
              <div className="instructor-stat-value">{submissions.length}</div>
              <div className="instructor-stat-sub">
                {scoredWorkLabel}
              </div>
            </div>
          </div>

          <div className="instructor-dimension-bar">
            <span className="instructor-dimension-label">Dimensions</span>
            {dimensionCategories.map((category) => {
              const categoryDimensions = courseDimensions.filter(
                (dimension, index) => getDimensionCategory(dimension, index) === category
              );

              if (categoryDimensions.length === 0) {
                return null;
              }

              return (
                <div className="instructor-dimension-group" key={category}>
                  <span className="instructor-dimension-category">
                    {category === 'Critical Awareness' ? 'Critical' : category}
                  </span>
                  {categoryDimensions.map((dimension) => {
                    const isActive = activeDimensionIds.includes(dimension.id);
                    const color = dimension.color_hex || categoryColors[category];

                    return (
                      <button
                        className={isActive ? 'active' : ''}
                        key={dimension.id}
                        onClick={() => handleDimensionToggle(dimension.id)}
                        style={
                          isActive
                            ? {
                                backgroundColor: `${color}14`,
                                borderColor: color,
                                color,
                              }
                            : undefined
                        }
                        type="button"
                      >
                        {dimension.short_label || dimension.label}
                      </button>
                    );
                  })}
                </div>
              );
            })}
            <div className="instructor-dimension-actions">
              <button onClick={() => setActiveDimensionIds(courseDimensionIds)} type="button">
                Select all
              </button>
              <button
                onClick={() => setActiveDimensionIds(courseDimensionIds)}
                type="button"
              >
                Reset
              </button>
            </div>
          </div>

          {courseLoading ? (
            <div className="instructor-card">
              <InstructorLoadingSkeleton
                message="Loading course analytics..."
                variant="dashboard"
              />
            </div>
          ) : (
            <div className="instructor-dashboard-grid-v2">
              <section className="instructor-card instructor-student-card">
                <div className="instructor-card-header">
                  <div>
                    <h2 className="instructor-card-title">
                      {scope === 'individual' ? 'Students' : 'Projects'}
                    </h2>
                    <div className="instructor-card-sub">
                      Click a row to view that profile - Score shown out of {maxScore}
                    </div>
                  </div>
                </div>
                <div className="instructor-table-scroll">
                  <table className="instructor-analytics-table">
                    <thead>
                      <tr>
                        <th>{scope === 'individual' ? 'Student' : 'Project'}</th>
                        <th>{scope === 'individual' ? 'Project Type' : 'Members'}</th>
                        <th># Subs</th>
                        <th>Last Submitted</th>
                        <th>Avg Score</th>
                        <th>Trend</th>
                        <th>Weakest Dim.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entities.length === 0 ? (
                        <tr>
                          <td className="table-empty" colSpan={7}>No analytics rows yet.</td>
                        </tr>
                      ) : (
                        entities.map((entity) => {
                          const averageScores = getAverageScoreMapForSnapshots(
                            entity.submissionSnapshots,
                            courseDimensionIds
                          );
                          const hasScoredSubmissions = entity.submissionSnapshots.some(
                            (snapshot) => snapshot.scoreCount > 0
                          );
                          const total = getScoreTotal(averageScores, activeDimensionIds);
                          const maxActiveScore = Math.max(activeDimensionIds.length * 3, 1);
                          const displayTotal = getScoreTotal(averageScores, courseDimensionIds);
                          const weakestDimension = hasScoredSubmissions
                            ? getWeakestDimension(averageScores, courseDimensions)
                            : null;
                          const trend = getTrend(entity, activeDimensionIds);

                          return (
                            <tr
                              className={entity.id === selectedEntityId ? 'selected' : ''}
                              key={entity.id}
                              onClick={() => handleEntitySelect(entity.id)}
                            >
                              <td>
                                <span className="instructor-person-cell">
                                  <span
                                    className="instructor-avatar"
                                    style={{ backgroundColor: entity.color }}
                                  >
                                    {entity.avatarLabel}
                                  </span>
                                  {entity.label}
                                </span>
                              </td>
                              <td>
                                {scope === 'individual' ? (
                                  <span
                                    className="instructor-group-badge"
                                    style={{ backgroundColor: entity.color }}
                                  >
                                    {entity.groupLabel}
                                  </span>
                                ) : (
                                  <div className="instructor-member-list">
                                    {entity.memberNames.slice(0, 6).map((memberName, memberIndex) => (
                                      <span
                                        className="instructor-member-name"
                                        key={`${memberName}-${memberIndex}`}
                                      >
                                        {memberName}
                                      </span>
                                    ))}
                                    {entity.memberNames.length > 6 && (
                                      <span
                                        className="instructor-member-overflow"
                                        title={entity.memberNames.slice(6).join(', ')}
                                      >
                                        +{entity.memberNames.length - 6} more
                                      </span>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td className="instructor-strong-cell">{entity.submissions}</td>
                              <td className="instructor-muted-cell">
                                {formatShortDate(entity.lastSubmitted)}
                              </td>
                              <td>
                                <span className="instructor-score-cell">
                                  <span className="instructor-score-bar wide">
                                    <span
                                      className="instructor-score-fill"
                                      style={{ width: `${(total / maxActiveScore) * 100}%` }}
                                    />
                                  </span>
                                  <strong>{displayTotal.toFixed(1)}/{maxScore}</strong>
                                </span>
                              </td>
                              <td>
                                <span className={`instructor-trend ${trend.toLowerCase()}`}>
                                  {trend === 'Up' ? '↑' : trend === 'Down' ? '↓' : '→'} {trend}
                                </span>
                              </td>
                              <td>
                                {weakestDimension ? (
                                  <span
                                    className="instructor-weak-tag"
                                    style={{
                                      backgroundColor: `${weakestDimension.color_hex}14`,
                                      borderColor: `${weakestDimension.color_hex}33`,
                                      color: weakestDimension.color_hex,
                                    }}
                                  >
                                    <span style={{ backgroundColor: weakestDimension.color_hex }} />
                                    {weakestDimension.short_label || weakestDimension.label}
                                  </span>
                                ) : (
                                  <span className="instructor-muted-cell">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="instructor-card instructor-radar-card">
                <div className="instructor-card-header">
                  <div>
                    <h2 className="instructor-card-title">Dimension Radar</h2>
                    <div className="instructor-card-sub">
                      {scope === 'individual'
                        ? 'Class average across all students'
                        : 'Project average across all projects'} - click a row in the table to compare
                    </div>
                  </div>
                </div>
                {selectedEntity && (
                  <div className="instructor-selected-bar">
                    Showing: <strong>{selectedEntity.label}</strong>
                    <button onClick={clearSelectedEntity} type="button">
                      {averageLabel} only
                    </button>
                  </div>
                )}
                <div className="instructor-submission-slider">
                  <div>
                    <span>Select Submission</span>
                    <span>{selectedSubmissionStatus}</span>
                  </div>
                  <div className="instructor-slider-track">
                    {submissionMarkers.map((marker, index) => {
                      const includedEntitiesForMarker = getScoredEntitiesForSubmissionIndex(
                        entities,
                        index
                      );
                      const markerTitle = selectedEntity
                        ? formatShortDate(marker.submittedAt)
                        : `${averageLabel} submission ${index + 1}. Based on ${getEntityCountLabel(
                            includedEntitiesForMarker.length,
                            scope
                          )}. Included: ${getIncludedEntitySummary(includedEntitiesForMarker)}.`;
                      const markerAriaLabel = selectedEntity
                        ? `Select submission ${index + 1} from ${formatShortDate(marker.submittedAt)}`
                        : `Select ${averageLabel} submission ${index + 1}, based on ${getEntityCountLabel(
                            includedEntitiesForMarker.length,
                            scope
                          )}`;

                      return (
                        <button
                          aria-label={markerAriaLabel}
                          className={index === activeSubmissionIndex ? 'active' : ''}
                          key={marker.id}
                          onClick={() => setRequestedSubmissionIndex(index)}
                          title={markerTitle}
                          type="button"
                        >
                          {index + 1}
                        </button>
                      );
                    })}
                  </div>
                  {!selectedEntity && submissionCount > 0 && (
                    <p className="instructor-submission-help">
                      {averageLabel} is calculated only from {scope === 'individual' ? 'students' : 'projects'} with a scored submission at the selected sequence.
                    </p>
                  )}
                </div>
                <InstructorRadarChart
                  className="instructor-radar-chartjs"
                  datasets={radarDatasets}
                  emptyMessage="Select at least two dimensions."
                  height={300}
                  labels={radarLabels}
                />
                <div className="instructor-radar-legend">
                  <span><i />{selectedEntity ? selectedEntity.label : 'No selection'}</span>
                  <span><i className="dashed" />{averageLabel}</span>
                </div>
              </section>

              <section className="instructor-card instructor-weakness-card">
                <div className="instructor-card-header">
                  <div>
                    <h2 className="instructor-card-title">Dimension Weakness Map</h2>
                    <div className="instructor-card-sub">
                      % of {scope === 'individual' ? 'students' : 'projects'} at each level
                    </div>
                  </div>
                  <div className="instructor-level-legend">
                    <span><i className="lv0" />Lv 0</span>
                    <span><i className="lv1" />Lv 1</span>
                    <span><i className="lv2" />Lv 2</span>
                    <span><i className="lv3" />Lv 3</span>
                  </div>
                </div>
                <div className="instructor-weakness-list">
                  {weaknessRows.map(({ dimension, counts, average }) => (
                    <div className="instructor-weakness-row" key={dimension.id}>
                      <div className="instructor-weakness-label">
                        <span style={{ backgroundColor: dimension.color_hex }} />
                        {dimension.short_label || dimension.label}
                      </div>
                      <div className="instructor-stacked-bar">
                        {counts.map((count, index) => {
                          const denominator = Math.max(scoredAnalyticsEntities.length, 1);
                          const percent = (count / denominator) * 100;

                          return percent > 0 ? (
                            <span className={`lv${index}`} key={`${dimension.id}-${index}`} style={{ width: `${percent}%` }}>
                              {Math.round(percent)}%
                            </span>
                          ) : null;
                        })}
                      </div>
                      <div className="instructor-weakness-score">
                        {average.toFixed(1)}<span>/3</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="instructor-card instructor-progression-card">
                <div className="instructor-card-header">
                  <div>
                    <h2 className="instructor-card-title">Dimension Progression</h2>
                    <div className="instructor-card-sub">
                      {progressionSubtitle}
                    </div>
                  </div>
                  <div className="instructor-dumbbell-legend">
                    <span><i className="previous" />Previous</span>
                    <span><i className="latest" />Latest</span>
                  </div>
                </div>
                <div className="instructor-dumbbell-scale">
                  <span />
                  <span>Lv. 0</span>
                  <span>Lv. 1</span>
                  <span>Lv. 2</span>
                  <span>Lv. 3</span>
                </div>
                <div className="instructor-progression-list">
                  {progressionRows.map(({ dimension, latest, previous }) => {
                    const previousPosition = (previous / 3) * 100;
                    const latestPosition = (latest / 3) * 100;
                    const leftPosition = Math.min(previousPosition, latestPosition);
                    const width = Math.abs(latestPosition - previousPosition);
                    const delta = latest - previous;

                    return (
                      <div className="instructor-progression-row" key={dimension.id}>
                        <div className="instructor-progression-label">
                          <span style={{ backgroundColor: dimension.color_hex }} />
                          {dimension.short_label || dimension.label}
                        </div>
                        <div className="instructor-dumbbell-track">
                          <span
                            className="line"
                            style={{
                              left: `${leftPosition}%`,
                              width: `${width}%`,
                            }}
                          />
                          <span className="point previous" style={{ left: `${previousPosition}%` }} />
                          <span className="point latest" style={{ left: `${latestPosition}%` }} />
                        </div>
                        <div className="instructor-delta">
                          {delta >= 0 ? '+' : ''}{delta.toFixed(2)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export { InstructorDashboard };
